import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRegistrationTemplate,
  deleteRegistrationTemplate,
  fetchRegistrationTemplates,
} from '~/api/bookkeeping/registration-templates';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { captureException } from '~/hooks/useExceptionLogger';
import {
  RegistrationTemplate,
  RegistrationTemplateCreate,
  RegistrationTemplateQueryKeys,
} from '~/types/registration-template';

export const useRegistrationTemplates = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: RegistrationTemplateQueryKeys.list(companyId),
    queryFn: () => fetchRegistrationTemplates(companyId),
    enabled: !!companyId,
  });
};

export const useCreateRegistrationTemplate = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (template: RegistrationTemplateCreate) => createRegistrationTemplate(companyId, template),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RegistrationTemplateQueryKeys.company(companyId) }),
    onError: (error) => {
      captureException(error);
    },
  });
};

export const useDeleteRegistrationTemplate = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (id: RegistrationTemplate['id']) => deleteRegistrationTemplate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RegistrationTemplateQueryKeys.company(companyId) }),
    onError: (error) => {
      captureException(error);
    },
  });
};
